// src/platform/storage/syncQueue.js

import indexedDb
  from '@/platform/storage/indexedDb'

import offlineManager
  from '@/platform/pwa/offlineManager'

import eventBus
  from '@/platform/core/eventBus'

import logger
  from '@/platform/core/logger'

import configuration
  from '@/platform/core/configuration'


class SyncQueue {
  constructor() {
    this.storeName =
      'syncQueue'

    this.processing =
      false

    this.processor =
      null

    this.initialized =
      false

    this.unsubscribeConnectivity =
      null
  }


  // ==========================================================
  // INITIALIZE
  // ==========================================================

  init() {
    if (this.initialized) {
      return
    }

    this.unsubscribeConnectivity =
      eventBus.on(
        'platform.offline.changed',
        event => {
          if (event?.isOnline) {
            this.process()
          }
        }
      )

    this.initialized =
      true

    logger.info(
      'Sync queue initialized.'
    )
  }


  // ==========================================================
  // DESTROY
  // ==========================================================

  destroy() {
    if (!this.initialized) {
      return
    }

    if (
      this.unsubscribeConnectivity
    ) {
      this.unsubscribeConnectivity()

      this.unsubscribeConnectivity =
        null
    }

    this.initialized =
      false

    logger.info(
      'Sync queue destroyed.'
    )
  }


  // ==========================================================
  // REGISTER PROCESSOR
  //
  // The processor may return:
  //
  // {
  //   removeFromQueue: true
  // }
  //
  // This is useful for POS transactions after the backend
  // confirms successful synchronization.
  // ==========================================================

  registerProcessor(
    processor
  ) {
    if (
      typeof processor !==
      'function'
    ) {
      throw new Error(
        '[SyncQueue] Processor must be a function.'
      )
    }

    this.processor =
      processor

    logger.info(
      'Sync queue processor registered.'
    )
  }


  // ==========================================================
  // ENQUEUE
  // ==========================================================

  async enqueue(
    job
  ) {
    if (!job?.type) {
      throw new Error(
        '[SyncQueue] Job type is required.'
      )
    }

    const now =
      Date.now()

    const record = {
      type:
        job.type,

      payload:
        job.payload
        ??
        null,

      status:
        'pending',

      retries:
        0,

      createdAt:
        now,

      updatedAt:
        now,

      metadata:
        job.metadata
        ??
        null
    }

    const id =
      await indexedDb.add(
        this.storeName,
        record
      )

    record.id =
      id

    logger.info(
      'Sync job queued.',
      {
        id,

        type:
          record.type,

        clientTransactionId:
          getClientTransactionId(
            record
          )
      }
    )

    eventBus.emit(
      'storage.sync.queued',
      {
        job:
          record
      }
    )

    /*
     * If currently online, try processing immediately.
     */
    if (
      offlineManager.isOnlineNow()
    ) {
      this.process()
    }

    return id
  }


  // ==========================================================
  // ENQUEUE POS TRANSACTION
  //
  // Prevent duplicate POS queue records for the same
  // ClientTransactionId.
  // ==========================================================

  async enqueuePosTransaction(
    job
  ) {
    const clientTransactionId =
      job?.metadata
        ?.clientTransactionId
      ??
      job?.payload
        ?.clientTransactionId
      ??
      null

    if (!clientTransactionId) {
      throw new Error(
        '[SyncQueue] POS ClientTransactionId is required.'
      )
    }

    const existing =
      await this.findByClientTransactionId(
        clientTransactionId
      )

    if (existing) {
      logger.info(
        'POS sync job already exists.',
        {
          id:
            existing.id,

          clientTransactionId
        }
      )

      /*
       * A previously failed POS job can safely be made
       * available for another synchronization attempt.
       */
      if (
        existing.status ===
        'failed'
      ) {
        existing.status =
          'pending'

        existing.retries =
          0

        existing.updatedAt =
          Date.now()

        existing.payload =
          job.payload
          ??
          existing.payload

        existing.metadata = {
          ...(existing.metadata || {}),
          ...(job.metadata || {}),

          clientTransactionId
        }

        await indexedDb.put(
          this.storeName,
          existing
        )

        if (
          offlineManager.isOnlineNow()
        ) {
          this.process()
        }
      }

      return existing.id
    }

    return await this.enqueue({
      ...job,

      metadata: {
        ...(job.metadata || {}),

        clientTransactionId
      }
    })
  }


  // ==========================================================
  // PROCESS QUEUE
  // ==========================================================

  async process() {
    if (this.processing) {
      return false
    }

    if (
      offlineManager.isOfflineNow()
    ) {
      logger.debug(
        'Sync processing skipped because application is offline.'
      )

      return false
    }

    if (!this.processor) {
      logger.warn(
        'Sync processing skipped because no processor is registered.'
      )

      return false
    }

    this.processing =
      true

    eventBus.emit(
      'storage.sync.started',
      {
        timestamp:
          new Date()
            .toISOString()
      }
    )

    try {
      const pending =
        await indexedDb.getAllByIndex(
          this.storeName,
          'status',
          'pending'
        )

      const priority = {
        POS_SESSION_OPEN:
          10,

        POS_SALE:
          20,

        POS_OFFLINE_PAYMENT:
          30,

        POS_SESSION_CLOSE:
          40
      };

      const orderedPending =
        [...pending]
          .sort(
            (
              left,
              right
            ) => {
              const leftPriority =
                priority[
                  left?.type
                ]
                ??
                100

              const rightPriority =
                priority[
                  right?.type
                ]
                ??
                100

              if (
                leftPriority !==
                rightPriority
              ) {
                return (
                  leftPriority -
                  rightPriority
                )
              }

              return (
                Number(
                  left?.createdAt
                  ??
                  0
                ) -
                Number(
                  right?.createdAt
                  ??
                  0
                )
              )
            }
          )

      for (
        const job
        of orderedPending
      ) {
        /*
         * Stop immediately if connectivity disappears
         * while processing.
         */
        if (
          offlineManager.isOfflineNow()
        ) {
          logger.warn(
            'Sync processing paused because connectivity was lost.'
          )

          break
        }

        await this.processJob(
          job
        )
      }

      eventBus.emit(
        'storage.sync.finished',
        {
          timestamp:
            new Date()
              .toISOString()
        }
      )

      return true
    }
    catch (error) {
      logger.error(
        'Sync queue processing failed.',
        {
          error
        }
      )

      eventBus.emit(
        'storage.sync.error',
        {
          error
        }
      )

      return false
    }
    finally {
      this.processing =
        false
    }
  }


  // ==========================================================
  // PROCESS ONE JOB
  //
  // IMPORTANT:
  //
  // Processor may return:
  //
  // {
  //   removeFromQueue: true
  // }
  //
  // In that case the queue record is physically removed after
  // the application processor has successfully finished.
  // ==========================================================

  async processJob(
    job
  ) {
    if (!job) {
      return false
    }

    const maxRetries =
      configuration.get(
        'sync.maxRetries',
        5
      )

    try {
      job.status =
        'processing'

      job.updatedAt =
        Date.now()

      await indexedDb.put(
        this.storeName,
        job
      )

      eventBus.emit(
        'storage.sync.job.started',
        {
          job
        }
      )

      // ========================================================
      // APPLICATION PROCESSOR
      // ========================================================

      const result =
        await this.processor(
          job
        )

      // ========================================================
      // DELETE AFTER AUTHORITATIVE SUCCESS
      //
      // POS synchronization can return removeFromQueue=true
      // after:
      //
      // 1. backend accepted ClientTransactionId
      // 2. sale was returned successfully
      // 3. local posTransactions record was deleted
      // ========================================================

      if (
        result?.removeFromQueue ===
        true
      ) {
        await indexedDb.delete(
          this.storeName,
          job.id
        )

        logger.info(
          'Sync job completed and removed.',
          {
            id:
              job.id,

            type:
              job.type,

            clientTransactionId:
              getClientTransactionId(
                job
              )
          }
        )

        eventBus.emit(
          'storage.sync.completed',
          {
            job,

            removed:
              true,

            result
          }
        )

        return true
      }

      // ========================================================
      // STANDARD COMPLETION
      // ========================================================

      job.status =
        'completed'

      job.updatedAt =
        Date.now()

      await indexedDb.put(
        this.storeName,
        job
      )

      logger.info(
        'Sync job completed.',
        {
          id:
            job.id,

          type:
            job.type
        }
      )

      eventBus.emit(
        'storage.sync.completed',
        {
          job,

          removed:
            false,

          result
        }
      )

      return true
    }
    catch (error) {
      job.retries =
        (
          job.retries
          ||
          0
        ) +
        1

      job.updatedAt =
        Date.now()

      if (
        job.retries >=
        maxRetries
      ) {
        job.status =
          'failed'
      }
      else {
        job.status =
          'pending'
      }

      await indexedDb.put(
        this.storeName,
        job
      )

      logger.error(
        'Sync job failed.',
        {
          id:
            job.id,

          type:
            job.type,

          clientTransactionId:
            getClientTransactionId(
              job
            ),

          retries:
            job.retries,

          maxRetries,

          error
        }
      )

      eventBus.emit(
        'storage.sync.failed',
        {
          job,
          error
        }
      )

      return false
    }
  }


  // ==========================================================
  // FIND BY CLIENT TRANSACTION ID
  //
  // The existing IndexedDB syncQueue schema does not have a
  // ClientTransactionId index, so this intentionally searches
  // all queue records.
  //
  // Queue size should remain small, and completed records can
  // still be periodically purged.
  // ==========================================================

  async findByClientTransactionId(
    clientTransactionId
  ) {
    if (!clientTransactionId) {
      return null
    }

    const jobs =
      await indexedDb.getAll(
        this.storeName
      )

    return (
      jobs.find(
        job =>
          idsMatch(
            getClientTransactionId(
              job
            ),
            clientTransactionId
          )
      )
      ??
      null
    )
  }


  // ==========================================================
  // FIND BY POS TRANSACTION KEY
  // ==========================================================

  async findByPosTransactionKey(
    transactionKey
  ) {
    if (!transactionKey) {
      return null
    }

    const jobs =
      await indexedDb.getAll(
        this.storeName
      )

    return (
      jobs.find(
        job =>
          idsMatch(
            job?.metadata
              ?.posTransactionKey
            ??
            job?.payload
              ?.posTransactionKey,
            transactionKey
          )
      )
      ??
      null
    )
  }


  // ==========================================================
  // REMOVE JOB
  // ==========================================================

  async remove(
    jobId
  ) {
    if (
      jobId === null ||
      jobId === undefined
    ) {
      return false
    }

    await indexedDb.delete(
      this.storeName,
      jobId
    )

    logger.info(
      'Sync job removed.',
      {
        id:
          jobId
      }
    )

    eventBus.emit(
      'storage.sync.removed',
      {
        id:
          jobId
      }
    )

    return true
  }


  // ==========================================================
  // REMOVE BY CLIENT TRANSACTION ID
  // ==========================================================

  async removeByClientTransactionId(
    clientTransactionId
  ) {
    if (!clientTransactionId) {
      return false
    }

    const jobs =
      await indexedDb.getAll(
        this.storeName
      )

    const matches =
      jobs.filter(
        job =>
          idsMatch(
            getClientTransactionId(
              job
            ),
            clientTransactionId
          )
      )

    for (
      const job
      of matches
    ) {
      await indexedDb.delete(
        this.storeName,
        job.id
      )
    }

    if (
      matches.length > 0
    ) {
      logger.info(
        'Sync jobs removed by ClientTransactionId.',
        {
          clientTransactionId,

          count:
            matches.length
        }
      )

      eventBus.emit(
        'storage.sync.removed',
        {
          clientTransactionId,

          count:
            matches.length
        }
      )
    }

    return (
      matches.length >
      0
    )
  }


  // ==========================================================
  // REMOVE BY POS TRANSACTION KEY
  // ==========================================================

  async removeByPosTransactionKey(
    transactionKey
  ) {
    if (!transactionKey) {
      return false
    }

    const jobs =
      await indexedDb.getAll(
        this.storeName
      )

    const matches =
      jobs.filter(
        job =>
          idsMatch(
            job?.metadata
              ?.posTransactionKey
            ??
            job?.payload
              ?.posTransactionKey,
            transactionKey
          )
      )

    for (
      const job
      of matches
    ) {
      await indexedDb.delete(
        this.storeName,
        job.id
      )
    }

    return (
      matches.length >
      0
    )
  }


  // ==========================================================
  // RETRY FAILED
  // ==========================================================

  async retryFailed() {
    const failed =
      await indexedDb
        .getAllByIndex(
          this.storeName,
          'status',
          'failed'
        )

    for (
      const job
      of failed
    ) {
      job.status =
        'pending'

      job.retries =
        0

      job.updatedAt =
        Date.now()

      await indexedDb.put(
        this.storeName,
        job
      )
    }

    logger.info(
      'Failed sync jobs reset.',
      {
        count:
          failed.length
      }
    )

    eventBus.emit(
      'storage.sync.retry',
      {
        count:
          failed.length
      }
    )

    if (
      failed.length >
      0
    ) {
      await this.process()
    }

    return failed.length
  }


  // ==========================================================
  // PURGE COMPLETED
  // ==========================================================

  async purgeCompleted() {
    const completed =
      await indexedDb
        .getAllByIndex(
          this.storeName,
          'status',
          'completed'
        )

    for (
      const job
      of completed
    ) {
      await indexedDb.delete(
        this.storeName,
        job.id
      )
    }

    logger.info(
      'Completed sync jobs purged.',
      {
        count:
          completed.length
      }
    )

    eventBus.emit(
      'storage.sync.purged',
      {
        count:
          completed.length
      }
    )

    return completed.length
  }


  // ==========================================================
  // GET QUEUE STATE
  // ==========================================================

  getPending() {
    return indexedDb
      .getAllByIndex(
        this.storeName,
        'status',
        'pending'
      )
  }


  getProcessing() {
    return indexedDb
      .getAllByIndex(
        this.storeName,
        'status',
        'processing'
      )
  }


  getFailed() {
    return indexedDb
      .getAllByIndex(
        this.storeName,
        'status',
        'failed'
      )
  }


  getCompleted() {
    return indexedDb
      .getAllByIndex(
        this.storeName,
        'status',
        'completed'
      )
  }

// ==========================================================
// GET ALL JOBS
//
// Used by synchronization management UI.
// ==========================================================

async getAll() {
  const jobs =
    await indexedDb.getAll(
      this.storeName
    );

  return Array.isArray(
    jobs
  )
    ? jobs
    : [];
}

  // ==========================================================
  // STATISTICS
  // ==========================================================

  async getStats() {
    const [
      pending,
      processing,
      failed,
      completed
    ] =
      await Promise.all([
        this.getPending(),
        this.getProcessing(),
        this.getFailed(),
        this.getCompleted()
      ])

    return {
      pending:
        pending.length,

      processing:
        processing.length,

      failed:
        failed.length,

      completed:
        completed.length,

      isProcessing:
        this.processing
    }
  }
}


// ============================================================
// CLIENT TRANSACTION ID
// ============================================================

function getClientTransactionId(
  job
) {
  return (
    job?.metadata
      ?.clientTransactionId
    ??
    job?.payload
      ?.clientTransactionId
    ??
    job?.payload
      ?.ClientTransactionId
    ??
    null
  )
}


// ============================================================
// ID COMPARISON
// ============================================================

function idsMatch(
  left,
  right
) {
  if (
    left === null ||
    left === undefined ||
    right === null ||
    right === undefined
  ) {
    return false
  }

  return (
    String(left) ===
    String(right)
  )
}


// ============================================================
// EXPORT
// ============================================================

const syncQueue =
  new SyncQueue()


export default syncQueue